import { useState, useMemo, useCallback, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { InlineNotification } from '@carbon/react';
import { OwnershipGraphResponse } from 'types/api-responses';
import { ValidationResults } from './v2-results-view';
import styles from './v2-results-view.module.scss';
import { getNodes, getEdges } from './data-helpers';
import { establishmentsArrayAtom } from 'atoms/establishments-atoms';
import { TitleChainData } from './types';
import { TitleChainValidatorFilters, LayoutSettings } from './v2-sidebar';
import { TitleValidatorDevNotice } from 'components/title-chain-validator/title-validator-dev-notice';

interface V2ResultsViewProps {
  ownershipGraph: OwnershipGraphResponse | null | undefined;
  documentTitle?: string;
  documentId?: number;
  documentHref?: string;
  reconcilerUrl?: string;
  /** Establishments this run was given. Zero means no starting ownership was
   * seeded, so nothing in the chain is rooted in a real owner. */
  selectedEstablishmentCount?: number;
}

export function V2ResultsView({
  ownershipGraph,
  documentTitle,
  documentId,
  documentHref,
  reconcilerUrl,
  selectedEstablishmentCount = 0,
}: V2ResultsViewProps) {
  const establishments = useAtomValue(establishmentsArrayAtom);
  const chainData = useMemo<TitleChainData | null>(() => {
    if (!ownershipGraph) return null;
    return {
      entityId: ownershipGraph.entityId,
      graph: ownershipGraph.graph,
      documents: ownershipGraph.documents,
      subjectAssetNodeIds: ownershipGraph.subjectAssetNodeIds ?? [],
      pathsToReview: ownershipGraph.pathsToReview,
    };
  }, [ownershipGraph]);

  // Layout settings — persisted to localStorage
  const LAYOUT_STORAGE_KEY = 'tv2-layout-settings-v2';
  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>(() => {
    const defaults: LayoutSettings = {
      layoutMode: 'side-by-side',
      showGraph: false,
      showTable: true,
      showCurrentOwnership: true,
      showRunsheet: true,
      showMap: true,
      graphDirection: 'vertical',
      consolidation: 'transaction',
    };
    try {
      const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch {
      // ignore
    }
    return defaults;
  });

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layoutSettings));
    } catch {
      // ignore
    }
  }, [layoutSettings]);

  // Filter state
  const defaultFilters: TitleChainValidatorFilters = useMemo(
    () => ({
      selectedLocations: [],
      interestTypeFilter: [],
      edgeTypeFilter: [],
    }),
    []
  );

  const [filters, setFilters] =
    useState<TitleChainValidatorFilters>(defaultFilters);

  // Dismissal lasts for the current result only — a fresh run re-raises it, so
  // closing it once doesn't hide the warning on the next validation.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  useEffect(() => {
    setBannerDismissed(false);
  }, [ownershipGraph]);

  const handleLayoutChange = useCallback((changes: Partial<LayoutSettings>) => {
    setLayoutSettings((prev) => ({ ...prev, ...changes }));
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<TitleChainValidatorFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters({
      selectedLocations: [],
      interestTypeFilter: [],
      edgeTypeFilter: [],
    });
  }, []);

  // Unique property names from graph nodes — used for the Properties filter dropdown
  const uniqueLocations = useMemo(() => {
    if (!chainData) return [];
    const locationSet = new Set<string>();
    getNodes(chainData.graph).forEach((node) => {
      const name = node.property.volume.geometryName?.trim();
      if (name) locationSet.add(name);
    });
    return Array.from(locationSet).sort();
  }, [chainData]);

  if (!chainData) {
    return null;
  }

  const noEstablishmentApplied = selectedEstablishmentCount === 0;
  const hasEstablishmentsAvailable = establishments.length > 0;
  // Blocked when no operation was valid, or when nothing ended up holding a
  // share — the graph filters zero-quantum nodes out, so it would draw empty.
  const hasNothingToGraph =
    !getEdges(chainData.graph).some((e) => e.isValid) ||
    !getNodes(chainData.graph).some((n) =>
      n.interests.some((i) => i.fractionF > 0)
    );

  return (
    <>
      {/* TODO(INS-11167): temporary. Remove <TitleValidatorDevNotice> + its
          import and delete title-validator-dev-notice.tsx once the full V2
          UX/backend feature branch ships. .bannerRow holds any number of
          banners side by side. */}
      <div className={styles.bannerRow}>
        <TitleValidatorDevNotice className={styles.noticeBanner} />
        {noEstablishmentApplied && !bannerDismissed && (
          <InlineNotification
            className={styles.bannerHalf}
            kind={hasEstablishmentsAvailable ? 'info' : 'warning'}
            lowContrast
            title={
              hasEstablishmentsAvailable
                ? 'Establishments auto-selected'
                : 'No establishment applied'
            }
            subtitle={
              hasEstablishmentsAvailable
                ? 'No establishment was selected, so the chain is rooted automatically to the available establishments.'
                : 'No establishment was applied, so nothing in this chain is rooted in a known owner.'
            }
            onCloseButtonClick={() => setBannerDismissed(true)}
          />
        )}
      </div>
      <div className={styles.resultsSlot}>
        <ValidationResults
          chainData={chainData}
          hasNothingToGraph={hasNothingToGraph}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onResetFilters={handleResetFilters}
          uniqueLocations={uniqueLocations}
          layoutSettings={layoutSettings}
          onLayoutChange={handleLayoutChange}
          documentTitle={documentTitle}
          documentId={documentId}
          documentHref={documentHref}
          reconcilerUrl={reconcilerUrl}
        />
      </div>
    </>
  );
}
